<template>
  <div class="md:col-span-2">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-1">
      <label class="flex flex-col gap-1 py-2">
        <span class="font-semibold text-black">{{ t('holderData.labels.name') }}</span>
        <input
          v-model="form.contact_name"
          type="text"
          :disabled="disabled || isSaving"
          :placeholder="t('holderData.placeholders.name')"
          class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 disabled:cursor-not-allowed disabled:bg-gray-100"
        >
      </label>

      <label class="flex flex-col gap-1 py-2">
        <span class="font-semibold text-black">{{ t('holderData.labels.surname') }}</span>
        <input
          v-model="form.contact_surname"
          type="text"
          :disabled="disabled || isSaving"
          :placeholder="t('holderData.placeholders.surname')"
          class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 disabled:cursor-not-allowed disabled:bg-gray-100"
        >
      </label>

      <label class="flex flex-col gap-1 py-2">
        <span class="font-semibold text-black">{{ t('holderData.labels.dni_nie') }}</span>
        <input
          v-model="form.dni_nie"
          type="text"
          :disabled="disabled || isSaving"
          :placeholder="t('holderData.placeholders.dni_nie')"
          class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 disabled:cursor-not-allowed disabled:bg-gray-100"
        >
      </label>

      <label class="flex flex-col gap-1 py-2">
        <span class="font-semibold text-black">{{ t('holderData.labels.telephone') }}</span>
        <!-- «No dispongo» marcado por el cliente: se ve aquí en lugar de un hueco
             vacío, y ADS puede cargar un teléfono encima (ADS-0172). -->
        <input
          v-model="form.telephone"
          type="tel"
          :disabled="disabled || isSaving"
          :placeholder="telephoneUnavailable ? t('holderData.telephoneUnavailable.label') : t('holderData.placeholders.telephone')"
          :class="telephoneUnavailable ? 'placeholder:text-[#385660] placeholder:font-semibold' : ''"
          class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 disabled:cursor-not-allowed disabled:bg-gray-100"
          data-testid="holder-telephone"
        >
        <span v-if="telephoneUnavailable" class="text-sm text-[#385660]" data-testid="holder-telephone-unavailable">
          {{ disabled ? t('holderData.telephoneUnavailable.declared') : t('holderData.telephoneUnavailable.hint') }}
        </span>
      </label>
    </div>

    <p v-if="errorMessage" class="pt-1 text-sm text-red-600" role="alert">{{ errorMessage }}</p>

    <div v-if="!disabled" class="flex justify-end pt-2">
      <!-- ButtonWhite ocupa todo el ancho por defecto; el contenedor lo ajusta al texto. -->
      <div class="w-fit">
        <ButtonWhite
          :disabled="isSaving"
          class="flex items-center gap-2"
          border-color="border-2 border-[#25B0D7]"
          text-color="text-[#25B0D7]"
          :title="isSaving ? t('holderData.saving') : t('holderData.save')"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue3-toastify';
import ButtonWhite from '@/components/button/ButtonWhite.vue';

const props = defineProps({
  requestData: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved']);
const { t } = useI18n();

const form = reactive({
  contact_name: '',
  contact_surname: '',
  dni_nie: '',
  telephone: '',
});
const isSaving = ref(false);
const errorMessage = ref('');
let synchronizedRequestId = null;
let lastSynced = { ...form };

const asText = (value) => String(value ?? '').trim();
const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

// El cliente declaró «No dispongo» y nadie ha cargado todavía un teléfono.
const telephoneUnavailable = computed(
  () => props.requestData?.telephone_unavailable === true && !asText(form.telephone)
);

const hasPendingEdits = () => Object.keys(lastSynced).some((field) => form[field] !== lastSynced[field]);

const applyValues = (values) => {
  Object.assign(form, values);
  lastSynced = { ...values };
};

// Los campos llegan rellenos con lo que ya consta en la solicitud. Mientras el
// gestor está editando no se sobrescribe lo que ha escrito.
watch(
  () => [
    props.requestData?.id,
    props.requestData?.contact_name,
    props.requestData?.contact_surname,
    props.requestData?.dni_nie,
    props.requestData?.telephone,
  ],
  ([requestId, contactName, contactSurname, dniNie, telephone]) => {
    if (requestId !== synchronizedRequestId) {
      synchronizedRequestId = requestId;
      errorMessage.value = '';
    } else if (hasPendingEdits()) {
      return;
    }

    applyValues({
      contact_name: asText(contactName),
      contact_surname: asText(contactSurname),
      dni_nie: asText(dniNie),
      telephone: asText(telephone),
    });
  },
  { immediate: true }
);

const save = async () => {
  if (!props.requestData?.id || props.disabled || isSaving.value) return;

  errorMessage.value = '';

  const payload = {};
  const fields = {
    contact_name: asText(form.contact_name),
    contact_surname: asText(form.contact_surname),
    dni_nie: asText(form.dni_nie),
  };

  for (const [field, value] of Object.entries(fields)) {
    if (value !== asText(props.requestData?.[field])) {
      payload[field] = value || null;
    }
  }

  const telephone = onlyDigits(form.telephone);
  if (telephone !== onlyDigits(props.requestData?.telephone)) {
    // El teléfono del titular es obligatorio salvo la excepción "No dispongo",
    // que solo puede declararse desde el formulario de la solicitud.
    if (!telephone) {
      errorMessage.value = t('holderData.errors.telephoneRequired');
      return;
    }
    if (telephone.length < 8 || telephone.length > 15) {
      errorMessage.value = t('holderData.errors.telephoneInvalid');
      return;
    }

    payload.telephone = telephone;
    if (props.requestData?.telephone_unavailable) {
      payload.telephone_unavailable = false;
    }
  }

  if (Object.keys(payload).length === 0) {
    toast.info(t('holderData.noChanges'));
    return;
  }

  isSaving.value = true;
  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/update/${props.requestData.id}`,
      payload,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    const updated = response.data?.value;
    if (!updated || typeof updated !== 'object') {
      toast.error(t('holderData.saveError'));
      return;
    }

    applyValues({
      contact_name: asText(updated.contact_name),
      contact_surname: asText(updated.contact_surname),
      dni_nie: asText(updated.dni_nie),
      telephone: asText(updated.telephone),
    });

    emit('saved', {
      contact_name: updated.contact_name ?? null,
      contact_surname: updated.contact_surname ?? null,
      dni_nie: updated.dni_nie ?? null,
      telephone: updated.telephone ?? null,
      telephone_unavailable: Boolean(updated.telephone_unavailable),
    });
    toast.success(t('holderData.saved'));
  } catch (error) {
    console.error('Error updating holder data:', error);
    const apiMessage = error?.response?.data?.message ?? error?.response?.data?.value;
    errorMessage.value = typeof apiMessage === 'string' ? apiMessage : '';
    toast.error(t('holderData.saveError'));
  } finally {
    isSaving.value = false;
  }
};
</script>
